import React, { useEffect, useState, useRef, useCallback } from "react";
import { debounce } from "lodash";

interface UseNearScreenOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  observeOnce?: boolean;
  observeChildren?: boolean;
  debouncedTime?: number;
}

export default function useNearScreen({
  root,
  rootMargin,
  threshold,
  observeOnce = true,
  observeChildren = false,
  debouncedTime = 200
}: UseNearScreenOptions = {}): [boolean, React.RefObject<Element>, number] {
  const [isNearScreen, setNearScreen] = useState<boolean>(false);
  const [intersectionRatio, setIntersectionRatio] = useState<number>(0);
  const observerRef = useRef<IntersectionObserver | undefined>();
  const fromRef = useRef<Element>(null);

  const defaultOnIntersection = useCallback(
    debounce((entries: IntersectionObserverEntry[]) => {
      const el = entries[0];

      if (el.isIntersecting) {
        setNearScreen(true);
        setIntersectionRatio(el.intersectionRatio);
        if (observeOnce && observerRef.current) observerRef.current.disconnect();
      } else if (!observeOnce) {
        setNearScreen(false);
        setIntersectionRatio(el.intersectionRatio);
      }
    }, debouncedTime),
    [observeOnce, debouncedTime]
  );

  useEffect(() => {
    const element = fromRef.current;
    observerRef.current = new IntersectionObserver(defaultOnIntersection, {
      root,
      rootMargin,
      threshold
    });
    const observer = observerRef.current;

    if (observer) {
      if (observeChildren && root?.children?.length) {
        Array.from(root.children).forEach(child => observer.observe(child));
      } else if (element) {
        observer.observe(element);
      }
    }

    return () => observer?.disconnect();
  }, [root, rootMargin, threshold, observeChildren, defaultOnIntersection]);

  return [isNearScreen, fromRef, intersectionRatio];
}
